// =====================================
// File 5: Updated src/components/Dashboard.js - Include ProjectsTab
// =====================================
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import useUrlState from '../hooks/useUrlState';
import Sidebar from './Sidebar';
import DashboardTab from './tabs/DashboardTab';
import AccountsTab from './tabs/AccountsTab';
import AccountTab from './tabs/AccountTab';
import ReceiptsPage from './ReceiptsPage';
import TransactionsPage from './TransactionsPage';
import CategoriesTab from './tabs/CategoriesTab';
import RecurringPaymentsTab from './tabs/RecurringPaymentsTab';
import AlertsTab from './tabs/AlertsTab';
import UsersTab from './tabs/UsersTab';
import SettingsTab from './tabs/SettingsTab';
import DadJokesPage from './DadJokesPage';
import CatPicturePage from './CatPicturePage';
import SmartQuotesPage from './SmartQuotesPage';
import ChickenPicturePage from './ChickenPicturePage';
import ProfilePage from './ProfilePage';
import SessionWarning from './common/SessionWarning';

const DashboardContent = ({ user, onLogout, accountId = null }) => {
  const navigate = useNavigate();

  // Calculate default tab only when no URL params exist - memoized to avoid recalculation
  const getDefaultTab = useMemo(() => {
    return () => {
      if (accountId) return 'account';
      // Check localStorage for previously active tab
      const savedTab = localStorage.getItem('activeTab');
      return savedTab || 'dashboard';
    };
  }, [accountId]);

  // Use URL state for all dashboard state that should persist
  const [urlState, setUrlState] = useUrlState({
    tab: getDefaultTab(),
    sidebar: true
  });

  const { tab: activeTab, sidebar: sidebarOpen } = urlState;

  // Clean up category param if not on transactions tab
  useEffect(() => {
    const currentParams = new URLSearchParams(window.location.search);
    if (currentParams.has('category') && activeTab !== 'transactions') {
      // Remove category param if we're not on the transactions tab
      setUrlState({ category: null });
    }
  }, [activeTab, setUrlState]);

  // Update localStorage whenever activeTab changes (as backup)
  useEffect(() => {
    if (activeTab && !accountId) {
      localStorage.setItem('activeTab', activeTab);
    }
  }, [activeTab, accountId]);


  // Handle tab change
  const handleSetActiveTab = useCallback((tabId) => {
    // If we're currently viewing an account, navigate back to main dashboard
    if (accountId) {
      navigate(`/?tab=${tabId}`);
      return;
    }

    // Update URL state
    setUrlState({ tab: tabId });
  }, [accountId, navigate, setUrlState]);


  const renderActiveTab = () => {
    // Check if trying to access admin-only tabs
    const isAdminTab = activeTab === 'users' || activeTab === 'settings';
    const isAdmin = user?.role === 'Administrator';

    // If non-admin tries to access admin tab, redirect to dashboard
    if (isAdminTab && !isAdmin) {
      // Redirect to dashboard
      setTimeout(() => {
        handleSetActiveTab('dashboard');
      }, 0);

      return (
        <div className="flex items-center justify-center h-full">
          <div className="text-center p-8 bg-red-50 dark:bg-red-900/20 rounded-lg border border-red-200 dark:border-red-800">
            <h2 className="text-2xl font-bold text-red-700 dark:text-red-400 mb-2">Access Denied</h2>
            <p className="text-red-600 dark:text-red-400">You don't have permission to access this page.</p>
            <p className="text-sm text-red-500 dark:text-red-500 mt-2">Redirecting to dashboard...</p>
          </div>
        </div>
      );
    }

    switch (activeTab) {
      case 'dashboard':
        return <DashboardTab />;
      case 'accounts':
        return <AccountsTab />;
      case 'account':
        return <AccountTab accountId={accountId} />;
      case 'transactions':
        return <TransactionsPage />;
      case 'receipts':
        return <ReceiptsPage />;
      case 'categories':
        return <CategoriesTab />;
      case 'recurring':
        return <RecurringPaymentsTab />;
      case 'alerts':
        return <AlertsTab />;
      case 'dadjokes':
        return <DadJokesPage />;
      case 'catpictures':
        return <CatPicturePage />;
      case 'smartquotes':
        return <SmartQuotesPage />;
      case 'chickenpictures':
        return <ChickenPicturePage />;
      case 'profile':
        return <ProfilePage currentUser={user} />;
      case 'users':
        return <UsersTab />;
      case 'settings':
        return <SettingsTab />;
      default:
        return <DashboardTab />;
    }
  };

  return (
    <div className="h-screen bg-gray-50 dark:bg-gray-900 flex transition-colors">
      <Sidebar
        sidebarOpen={sidebarOpen}
        setSidebarOpen={(open) => setUrlState({ sidebar: open })}
        activeTab={activeTab}
        setActiveTab={handleSetActiveTab}
        user={user}
        onLogout={onLogout}
      />

      <div className="flex-1 flex flex-col">
        <div className="flex-1 pt-14 pb-16 lg:pt-0 lg:pb-0 overflow-y-auto">
          {renderActiveTab()}
        </div>
      </div>
      
      {/* Session Warning */}
      <SessionWarning
        onSessionExtended={() => {
        }}
        onLogout={() => {
          onLogout?.();
        }}
      />
    </div>
  );
};

const Dashboard = (props) => {
  return <DashboardContent {...props} />;
};

export default Dashboard;
